import { MoveNetPoseEngine, type MoveNetOptions } from './MoveNetPoseEngine';
import { MOVENET_SKELETON } from './MoveNetSkeleton';
import {
  sourceHeight,
  sourceWidth,
  type PoseEngine,
  type PoseSource,
  type RawPose,
  type RawPoseResult,
  type SkeletonDefinition,
} from './PoseEngine';

const LIGHTNING_URL =
  'https://tfhub.dev/google/tfjs-model/movenet/singlepose/lightning/4/default/1';
const THUNDER_URL = 'https://tfhub.dev/google/tfjs-model/movenet/singlepose/thunder/4/default/1';

interface WorkerResponse {
  reqId: number;
  ok: boolean;
  error?: string;
  poses?: { landmarks: { x: number; y: number; score: number }[] }[];
  inferMs?: number;
}

export class MoveNetPoseEngineWorker implements PoseEngine {
  readonly id = 'movenet-singlepose-worker';
  readonly label: string;
  readonly skeleton: SkeletonDefinition = MOVENET_SKELETON;
  readonly supportsMultiPose = false;
  private worker: Worker | null = null;
  private reqId = 0;
  private pending = new Map<
    number,
    { resolve: (v: WorkerResponse) => void; reject: (e: Error) => void }
  >();
  private fallback: MoveNetPoseEngine | null = null;
  private readonly kind: 'lightning' | 'thunder';
  private readonly inputSize: number;

  constructor(private readonly opts: MoveNetOptions = {}) {
    this.kind = opts.modelType === 'thunder' ? 'thunder' : 'lightning';
    this.inputSize = this.kind === 'thunder' ? 256 : 192;
    this.label =
      this.kind === 'thunder' ? 'MoveNet Thunder worker (17pt)' : 'MoveNet Lightning worker (17pt)';
  }

  async initialize(): Promise<void> {
    try {
      const worker = new Worker(new URL('../workers/movenet.worker.ts', import.meta.url), {
        type: 'module',
      });
      worker.onmessage = (ev: MessageEvent<WorkerResponse>) => this.handleMessage(ev.data);
      worker.onerror = (ev) => {
        console.error('[MoveNetPoseEngineWorker] worker error', ev.message);
        for (const p of this.pending.values()) p.reject(new Error('worker error'));
        this.pending.clear();
      };
      this.worker = worker;
      await this.request({
        type: 'init',
        modelUrl: this.kind === 'thunder' ? THUNDER_URL : LIGHTNING_URL,
        inputSize: this.inputSize,
        kind: this.kind,
      });
    } catch (err) {
      console.warn('[MoveNetPoseEngineWorker] falling back to main-thread MoveNet:', err);
      this.worker?.terminate();
      this.worker = null;
      this.fallback = new MoveNetPoseEngine(this.opts);
      await this.fallback.initialize();
    }
  }

  async setNumPoses(_n: number): Promise<void> {
    // SinglePose only; ignore.
  }

  async detectForVideo(source: PoseSource, _timestamp: number): Promise<RawPoseResult> {
    if (this.fallback) return this.fallback.detectForVideo(source, _timestamp);
    if (!this.worker) throw new Error('worker not initialized');
    const w = sourceWidth(source) || 1;
    const h = sourceHeight(source) || 1;
    if (w <= 0 || h <= 0) return { poses: [] };
    const bitmap = await createImageBitmap(source as CanvasImageSource);
    const res = await this.request({ type: 'detect', bitmap }, [bitmap]);
    const poses: RawPose[] = (res.poses ?? []).map((p) => ({
      landmarks: p.landmarks.map((l) => ({ x: l.x, y: l.y, score: l.score })),
    }));
    return { poses };
  }

  dispose(): void {
    this.fallback?.dispose();
    this.fallback = null;
    if (!this.worker) return;
    const w = this.worker;
    this.worker = null;
    for (const p of this.pending.values()) p.reject(new Error('disposed'));
    this.pending.clear();
    try {
      w.postMessage({ type: 'dispose', reqId: 0 });
    } catch {
      // ignore
    }
    w.terminate();
  }

  private request(
    msg: Record<string, unknown>,
    transfer: Transferable[] = [],
  ): Promise<WorkerResponse> {
    const id = ++this.reqId;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker!.postMessage({ ...msg, reqId: id }, transfer);
    });
  }

  private handleMessage(data: WorkerResponse): void {
    const entry = this.pending.get(data.reqId);
    if (!entry) return;
    this.pending.delete(data.reqId);
    if (data.ok) entry.resolve(data);
    else entry.reject(new Error(data.error ?? 'worker error'));
  }
}
